'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ChevronLeft, SaveAltSharp } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { format } from 'date-fns';

import {
  ReportsBannerList,
  useBreadcrumbs,
  useDownloadReportsBanner,
  useGetAdvertiser,
  useGetBannersKeys,
  useGetCampaign,
  useGetReportsBanner,
} from '~/entities';
import {
  ContentTitle,
  Pagination,
  StyledContainer,
  StyledTableContainer,
} from '~/shared';
import { ReportsBannerDto } from '~/types';

export type RelatoriosBannerProps = {
  advertiserId: string;
  campaignId: string;
  bannerKey: string;
};

const REPORTS_CAMPAIGNS_PAGE_SIZE = 15;

export function RelatoriosBannerView({
  advertiserId,
  campaignId,
  bannerKey,
}: RelatoriosBannerProps) {
  const breadcrumbs = useBreadcrumbs();
  const getBannersKeys = useGetBannersKeys();
  const bannerKeyData = getBannersKeys.data?.options.find(
    option => option.key === bannerKey
  );

  const [reportsFilter, setReportsFilter] = useState<ReportsBannerDto>({
    idCampaign: campaignId,
    key: bannerKey,
    count: REPORTS_CAMPAIGNS_PAGE_SIZE,
    page: 1,
  });

  const getAdvertiserQuery = useGetAdvertiser(advertiserId);
  const getCampaignQuery = useGetCampaign(campaignId);

  const getReportsBannerQuery = useGetReportsBanner(reportsFilter);
  const { isLoading, isFetching, data } = getReportsBannerQuery;

  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / REPORTS_CAMPAIGNS_PAGE_SIZE);

  const advertiserTitle = getAdvertiserQuery.data?.name ?? 'Carregando';
  const campaignTitle = getCampaignQuery.data?.name ?? 'Carregando';
  const bannerTitle = bannerKeyData?.name ?? 'Carregando';

  useEffect(() => {
    breadcrumbs.setPaths([
      { label: 'Relatórios', path: '/relatorios' },
      { label: advertiserTitle, path: `/relatorios/${advertiserId}` },
      {
        label: campaignTitle ?? 'Carregando',
        path: `/relatorios/${advertiserId}/${campaignId}`,
      },
      {
        label: bannerTitle,
        path: `/relatorios/${advertiserId}/${campaignId}/${bannerKey}`,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advertiserTitle, campaignTitle]);

  const downloadReportsBannerMutation = useDownloadReportsBanner(res => {
    const nowFormatted = format(new Date(), 'dd-MM-yyyy HH-mm');
    const filename = `Relatório Banner - ${bannerKeyData?.name} - ${getCampaignQuery.data?.name ?? 'Sem Nome'} - ${nowFormatted}.xlsx`;
    const blob = new Blob([res.data], { type: res.headers['content-type'] });
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    a.click();
  });

  function handleExportExcel() {
    downloadReportsBannerMutation.mutate(reportsFilter);
  }

  function nextPageHandler() {
    if (reportsFilter.page < totalPages) {
      setReportsFilter(state => ({
        ...state,
        page: state.page + 1,
      }));
    }
  }

  function prevPageHandler() {
    if (reportsFilter.page > 1) {
      setReportsFilter(state => ({
        ...state,
        page: state.page - 1,
      }));
    }
  }

  return (
    <>
      <ContentTitle title="Relatórios" isFetching={isLoading || isFetching} />

      <StyledContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Link href={`/relatorios/${advertiserId}/${campaignId}`} passHref>
            <Button variant="text" color="primary">
              <ChevronLeft />
              Voltar
            </Button>
          </Link>
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ textTransform: 'none' }}
            onClick={handleExportExcel}
            startIcon={
              downloadReportsBannerMutation.isPending ? (
                <CircularProgress size={15} />
              ) : (
                <SaveAltSharp />
              )
            }
            disabled={downloadReportsBannerMutation.isPending}
          >
            Baixar
          </Button>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography
            variant="h5"
            sx={theme => ({
              fontSize: theme.typography.pxToRem(18),
              fontWeight: 500,
              color: '#BDBDBD',
            })}
          >
            {getAdvertiserQuery.data?.name ?? 'Carregando...'}
          </Typography>
          <Typography
            variant="h4"
            sx={theme => ({
              fontSize: theme.typography.pxToRem(26),
              fontWeight: 500,
            })}
          >
            {getCampaignQuery.data?.name ?? 'Carregando...'}
          </Typography>
          <Typography
            variant="h3"
            color="primary"
            sx={theme => ({
              fontSize: theme.typography.pxToRem(28),
              fontWeight: 500,
              mt: 4,
              mb: 2,
            })}
          >
            {bannerKeyData?.name ?? 'Carregando...'}
          </Typography>
        </Box>

        <StyledTableContainer>
          <ReportsBannerList
            advertiserId={advertiserId}
            campaignId={campaignId}
            bannerKey={bannerKeyData}
            getReportsBannerQuery={getReportsBannerQuery}
          />
        </StyledTableContainer>
        <Pagination
          disabled={totalPages === 1 || isLoading || total === 0}
          page={1}
          count={1}
          nextPage={nextPageHandler}
          prevPage={prevPageHandler}
          onChange={(_, newPage) =>
            setReportsFilter(state => ({
              ...state,
              page: newPage,
            }))
          }
        />
      </StyledContainer>
    </>
  );
}
